import cx from "classnames";

import { IPaginator } from "common/types";

import Pagination from "client/common/components/layouts/Pagination";
import { useBreakpoints } from "client/common/hooks";
import { ProductListSource } from "client/analytics/types";
import {
  ProductListContext,
  useProductsViewOnVisible,
  useViewItemListOnVisible,
} from "client/analytics/utils";

import { ProductSearchResult } from "../types";
import ProductCard from "./ProductCard";
import ProductCardMobile from "./ProductCardMobile";

import styles from "../styles/ProductCardList.module.less";

type ProductCardListProps = {
  results: ProductSearchResult[];
  listSource: ProductListSource;
  paginator: IPaginator;
  onPageChange: (page: number) => void;
  entityName: string;
  listContext?: ProductListContext;
  className?: string;
  /** Set when the filter panel sits beside the grid; drops one column. */
  isNarrow?: boolean;
  /** The cards are the previous filters' results while the new ones load. */
  isStale?: boolean;
};

/**
 * A page of product cards and its pagination: a grid on desktop, a stacked list on mobile. Reports
 * its own impressions, so callers only supply the data and the list's analytics source.
 */
const ProductCardList = ({
  results,
  listSource,
  paginator,
  onPageChange,
  entityName,
  listContext,
  className,
  isNarrow = false,
  isStale = false,
}: ProductCardListProps) => {
  const { isTabletPortraitAndSmaller } = useBreakpoints();
  const itemListRef = useViewItemListOnVisible(results, listSource);
  const productsViewRef = useProductsViewOnVisible(results, listSource, listContext);

  const renderCards = () => {
    if (isTabletPortraitAndSmaller) {
      return (
        <div
          ref={productsViewRef}
          className={cx(styles.list, isStale && styles.stale)}
          inert={isStale}
        >
          {results.map((result) => (
            <ProductCardMobile
              key={result.id}
              result={result}
              listSource={listSource}
              collectionSlug={listContext?.collectionSlug}
              categoryId={listContext?.categoryId}
            />
          ))}
        </div>
      );
    }

    return (
      <div
        ref={productsViewRef}
        className={cx(styles.grid, isNarrow && styles.gridNarrow, isStale && styles.stale)}
        inert={isStale}
      >
        {results.map((result) => (
          <ProductCard
            key={result.id}
            result={result}
            listSource={listSource}
            collectionSlug={listContext?.collectionSlug}
            categoryId={listContext?.categoryId}
          />
        ))}
      </div>
    );
  };

  return (
    <div ref={itemListRef} className={cx(styles.section, className)}>
      {renderCards()}
      <Pagination paginator={paginator} onPageChange={onPageChange} entityName={entityName} />
    </div>
  );
};

export default ProductCardList;
